/**
 * The key art, and the one place its paths are written down.
 *
 * Two images and an icon, all under `public/art/`. They follow the convention
 * `audio/music.ts` already set for the three music tracks: a file in `public/`
 * plus `import.meta.env.BASE_URL`, rather than a bundler import. The reason is the
 * same one — the HUD is served under `/grind/hud/` in **both** dev (Caddy → Vite)
 * and production (`r.Static("/grind/hud", webDist)` in `main.go`), so every asset
 * URL has to carry that prefix, and `BASE_URL` is where Vite keeps it.
 *
 * ── Why the game ships any art at all ───────────────────────────────────────
 *
 * It is a deliberate and narrow exception. Everything the game *simulates* is
 * procedural — meshes, textures, sound effects — and that is a design pillar, not
 * an accident of not having an artist. These three files simulate nothing: they are
 * the wordmark, the backdrop behind the main menu, and the browser-tab icon. The
 * title screen's whole job is to say what kind of game this is before a frame of it
 * has been played, and the forged logo does that in a way no webfont plus a
 * gradient can — the letterforms exist in no system font and the magma seam glows
 * *inside* each letter.
 *
 * 350 KB for all four files, as WebP — of which the backdrop's two sizes are 280 KB and
 * only one is ever fetched. Nothing in the running game depends on any of them: the scene
 * renders identically with `public/art/` deleted, and the title screen falls back to its
 * `alt` text.
 */

/** The files, relative to the Vite base. */
export const ART = {
  /** The forged wordmark, 900 × 368 with alpha. */
  logo: 'art/logo.webp',
  /**
   * The siege at the ruined keep, behind the main menu.
   *
   * **Two sizes, and the browser picks one.** The source art is 5632 × 3072 and 8.9 MB,
   * which is not a thing to put on a title screen: it is drawn behind five scrims at
   * `brightness(0.62)`, so detail past a couple of thousand pixels is spent on nothing.
   * These are 2560 × 1396 (203 KB) and 1280 × 698 (78 KB), and `TitleScreen` offers both
   * through `srcset` so a laptop never downloads the large one.
   *
   * It replaced `title-bg.webp`, the warm anvil-and-magma valley, which is **deleted**
   * rather than left beside it — an unreferenced asset is weight nobody can tell is dead.
   */
  titleBackdrop: 'art/title-bg-v2-2560.webp',
  titleBackdropSmall: 'art/title-bg-v2-1280.webp',
  /** The `G` from the wordmark, 128 × 128. Referenced from `index.html`, not here. */
  favicon: 'art/favicon.png',
} as const;

/**
 * The backdrop's `srcset`, with each candidate's real pixel width.
 *
 * Built here rather than spelled in the component so the widths cannot drift from the
 * files: `w` descriptors are a promise about the image, and a wrong one makes the browser
 * choose badly at every viewport rather than failing visibly.
 */
export function backdropSrcSet(base?: string): string {
  return [
    `${artUrl(ART.titleBackdropSmall, base)} 1280w`,
    `${artUrl(ART.titleBackdrop, base)} 2560w`,
  ].join(', ');
}

/**
 * Joins the Vite base onto one of the paths above.
 *
 * The base is `/grind/hud/` in every real environment and `/` under vitest, and it
 * is documented to carry a trailing slash — but a missing or doubled one would
 * produce a 404 that looks exactly like a missing file, so both are normalised
 * here rather than assumed. That is the whole reason this is a function.
 */
export function artUrl(file: string, base: string = import.meta.env.BASE_URL): string {
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return prefix + file.replace(/^\/+/, '');
}
